/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { tokens } from '@neo4j-ndl/base';
import classNames from 'classnames';
import {
  type ClearIndicatorProps,
  components,
  type ControlProps,
  type DropdownIndicatorProps,
  type GroupBase,
  type IndicatorsContainerProps,
  type InputProps,
  type MenuProps,
  type MultiValueProps,
  type OptionProps,
  type PlaceholderProps,
  type SingleValueProps,
  type ValueContainerProps,
} from 'react-select';
import {
  type MenuListProps,
  type MenuPortalProps,
} from 'react-select/dist/declarations/src/components/Menu';

import { Checkbox } from '../checkbox';
import { ChevronDownIconOutline, XMarkIconOutline } from '../icons';
import { Tag } from '../tag';
import { useNeedleTheme } from '../theme';
import { type SelectOverrideCustomProps } from './types';

export const DropdownIndicatorCurrying = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  size,
}: SelectOverrideCustomProps<OptionType, IsMulti, GroupType>) =>
  function Component(
    props: DropdownIndicatorProps<OptionType, IsMulti, GroupType>,
  ) {
    const classes = classNames('ndl-select-dropdown-indicator', {
      'ndl-is-multi': props.isMulti,
      'ndl-disabled': props.isDisabled,
      'ndl-focused': props.isFocused,
      'ndl-small': size === 'small',
      'ndl-medium': size === 'medium',
      'ndl-large': size === 'large',
    });

    return (
      <components.DropdownIndicator {...props} className={classes}>
        <ChevronDownIconOutline
          className="ndl-select-icon"
          style={{
            transform: props.selectProps.menuIsOpen
              ? 'rotate(180deg)'
              : 'rotate(0deg)',
            transitionDuration: tokens.motion.duration.quick,
          }}
        />
      </components.DropdownIndicator>
    );
  };

export const CustomClearIndication = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  size,
}: SelectOverrideCustomProps<OptionType, IsMulti, GroupType>) =>
  function Component({
    className,
    ...restProps
  }: ClearIndicatorProps<OptionType, IsMulti, GroupType>) {
    const classes = classNames('ndl-select-clear', className, {
      'ndl-is-multi': restProps.isMulti,
      'ndl-focused': restProps.isFocused,
      'ndl-small': size === 'small',
      'ndl-medium': size === 'medium',
      'ndl-large': size === 'large',
    });
    return (
      <components.ClearIndicator {...restProps} className={classes}>
        <div>
          <XMarkIconOutline />
          <div className="ndl-select-divider" />
        </div>
      </components.ClearIndicator>
    );
  };

export const CustomMultiValue = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  size,
}: SelectOverrideCustomProps<OptionType, IsMulti, GroupType>) =>
  function Component({
    children,
    className,
    ...restProps
  }: MultiValueProps<OptionType, IsMulti, GroupType>) {
    const classes = classNames('ndl-select-multi-value', className, {
      'ndl-is-multi': restProps.isMulti,
      'ndl-disabled': restProps.isDisabled,
      'ndl-focused': restProps.isFocused,
      'ndl-small': size === 'small',
      'ndl-medium': size === 'medium',
      'ndl-large': size === 'large',
    });
    return (
      <components.MultiValueContainer
        {...restProps}
        innerProps={{
          className: classes,
        }}
      >
        <Tag
          size={size}
          onClick={(e) => {
            e.preventDefault();
            restProps.removeProps.onClick?.(
              e as unknown as React.MouseEvent<HTMLDivElement, MouseEvent>,
            );
          }}
          htmlAttributes={{
            onMouseDown: (e) => e.preventDefault(),
          }}
        >
          {children}
        </Tag>
      </components.MultiValueContainer>
    );
  };

export const CustomPlaceholder = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  size,
}: SelectOverrideCustomProps<OptionType, IsMulti, GroupType>) =>
  function Component({
    children,
    className,
    ...restProps
  }: PlaceholderProps<OptionType, IsMulti, GroupType>) {
    const classes = classNames('ndl-select-placeholder', className, {
      'ndl-is-multi': restProps.isMulti,
      'ndl-disabled': restProps.isDisabled,
      'ndl-focused': restProps.isFocused,
      'ndl-small': size === 'small',
      'ndl-medium': size === 'medium',
      'ndl-large': size === 'large',
    });
    return (
      <components.Placeholder {...restProps} className={classes}>
        {children}
      </components.Placeholder>
    );
  };

export const CustomControl = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  size,
  isClean,
  errorText,
}: SelectOverrideCustomProps<OptionType, IsMulti, GroupType>) =>
  function Component({ children, className, ...restProps }: ControlProps) {
    const classes = classNames('ndl-select-control', className, {
      'ndl-is-multi': restProps.isMulti,
      'ndl-disabled': restProps.isDisabled,
      'ndl-focused': restProps.isFocused,
      'ndl-small': size === 'small',
      'ndl-medium': size === 'medium',
      'ndl-large': size === 'large',
      'ndl-clean': isClean,
      'ndl-has-error': !!errorText,
    });

    return (
      <components.Control {...restProps} className={classes}>
        {children}
      </components.Control>
    );
  };

export const CustomIndicatorsContainer = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  size,
}: SelectOverrideCustomProps<OptionType, IsMulti, GroupType>) =>
  function Component({
    children,
    className,
    ...restProps
  }: IndicatorsContainerProps) {
    const classes = classNames('ndl-select-indicators-container', className, {
      'ndl-is-multi': restProps.isMulti,
      'ndl-disabled': restProps.isDisabled,
      'ndl-small': size === 'small',
      'ndl-medium': size === 'medium',
      'ndl-large': size === 'large',
    });
    return (
      <components.IndicatorsContainer {...restProps} className={classes}>
        {children}
      </components.IndicatorsContainer>
    );
  };

export const CustomValueContainer = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  size,
}: SelectOverrideCustomProps<OptionType, IsMulti, GroupType>) =>
  function Component({
    children,
    className,
    ...restProps
  }: ValueContainerProps) {
    const classes = classNames('ndl-select-value-container', className, {
      'ndl-is-multi': restProps.isMulti,
      'ndl-disabled': restProps.isDisabled,
      'ndl-small': size === 'small',
      'ndl-medium': size === 'medium',
      'ndl-large': size === 'large',
    });
    return (
      <components.ValueContainer {...restProps} className={classes}>
        {children}
      </components.ValueContainer>
    );
  };

export const CustomInput = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  size,
}: SelectOverrideCustomProps<OptionType, IsMulti, GroupType>) =>
  function Component({ className, ...restProps }: InputProps) {
    const classes = classNames('ndl-select-input', className, {
      'ndl-is-multi': restProps.isMulti,
      'ndl-disabled': restProps.isDisabled,
      'ndl-small': size === 'small',
      'ndl-medium': size === 'medium',
      'ndl-large': size === 'large',
    });
    return <components.Input {...restProps} className={classes} />;
  };

export const CustomSingleValue = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  size,
}: SelectOverrideCustomProps<OptionType, IsMulti, GroupType>) =>
  function Component({ className, ...restProps }: SingleValueProps) {
    const classes = classNames('ndl-select-single-value', className, {
      'ndl-is-multi': restProps.isMulti,
      'ndl-disabled': restProps.isDisabled,
      'ndl-small': size === 'small',
      'ndl-medium': size === 'medium',
      'ndl-large': size === 'large',
    });
    return <components.SingleValue {...restProps} className={classes} />;
  };

/** Overrides that are part of the popover menu */

export const CustomMenu = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  size,
}: SelectOverrideCustomProps<OptionType, IsMulti, GroupType>) =>
  function Component({ children, className, ...restProps }: MenuProps) {
    const { themeClassName } = useNeedleTheme();
    const classes = classNames('ndl-select-menu', className, themeClassName, {
      'ndl-is-multi': restProps.isMulti,
      'ndl-small': size === 'small',
      'ndl-medium': size === 'medium',
      'ndl-large': size === 'large',
    });
    return (
      <components.Menu {...restProps} className={classes}>
        {children}
      </components.Menu>
    );
  };

export const CustomMenuPortal = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  size,
}: SelectOverrideCustomProps<OptionType, IsMulti, GroupType>) =>
  function Component({
    children,
    className,
    ...restProps
  }: MenuPortalProps<OptionType, IsMulti, GroupType>) {
    const classes = classNames(className, 'ndl-select-menu-portal', {
      'ndl-is-multi': restProps.isMulti,
      'ndl-small': size === 'small',
      'ndl-medium': size === 'medium',
      'ndl-large': size === 'large',
    });
    return (
      <components.MenuPortal {...restProps} className={classes}>
        {children}
      </components.MenuPortal>
    );
  };

export const CustomMenuList = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  size,
}: SelectOverrideCustomProps<OptionType, IsMulti, GroupType>) =>
  function Component({
    children,
    className,
    ...restProps
  }: MenuListProps<OptionType, IsMulti, GroupType>) {
    const classes = classNames(className, 'ndl-select-menu-list', {
      'ndl-is-multi': restProps.isMulti,
      'ndl-small': size === 'small',
      'ndl-medium': size === 'medium',
      'ndl-large': size === 'large',
    });
    return (
      <components.MenuList {...restProps} className={classes}>
        {children}
      </components.MenuList>
    );
  };

export const CustomOption = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  size,
}: SelectOverrideCustomProps<OptionType, IsMulti, GroupType>) =>
  function Component({
    children,
    className,
    ...restProps
  }: OptionProps<OptionType, IsMulti, GroupType>) {
    const classes = classNames(className, 'ndl-select-option', {
      'ndl-is-multi': restProps.isMulti,
      'ndl-disabled': restProps.isDisabled,
      'ndl-selected': restProps.isSelected,
      'ndl-focused': restProps.isFocused,
      'ndl-small': size === 'small',
      'ndl-medium': size === 'medium',
      'ndl-large': size === 'large',
    });
    return (
      <components.Option {...restProps} className={classes}>
        {restProps.isMulti === true && (
          <Checkbox
            isChecked={restProps.isSelected}
            ariaLabel={restProps.label}
            isDisabled={restProps.isDisabled}
            htmlAttributes={{
              onPointerDown: (e) => {
                e.preventDefault();
                restProps.innerProps.onClick?.(e);
              },
              onTouchStart: (e) => {
                e.preventDefault();
                restProps.innerProps.onClick?.(
                  e as unknown as React.MouseEvent<HTMLDivElement, MouseEvent>,
                );
              },
              disabled: true,
              'aria-hidden': true,
              role: 'presentation',
              tabIndex: -1,
            }}
          />
        )}
        {children}
      </components.Option>
    );
  };
